import apiClient from './api';
import axios from 'axios';
import config from '../config';

// Önbellek mekanizması
const cache = {
  popularLocations: null
};

// Hata loglama yardımcı fonksiyonu
const logError = (error) => {
  console.error('Error in popularLocationService:', error.message);
  if (error.response) {
    console.error('Status:', error.response.status);
    console.error('Data:', error.response.data);
  }
};

// Duplicate verileri filtreleme fonksiyonu
const filterUniqueLocations = (locations) => {
  if (!locations || !Array.isArray(locations)) return [];
  
  const uniqueNames = new Set();
  return locations.filter(location => {
    if (!location.name) return false;
    
    const normalizedName = location.name.toLowerCase().trim();
    if (uniqueNames.has(normalizedName)) return false;
    
    uniqueNames.add(normalizedName);
    return true;
  });
};

const getPopularLocations = async (latitude, longitude) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
            `${config.API_BASE_URL_V1}auto-complete/point-of-interest`, 
            { latitude, longitude },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }
        );

        // Filter out non-tourist locations
        const touristLocations = response.data.data.filter(location => {
            const validTypes = [
                'tourist_attraction',
                'museum',
                'mosque',
                'park',
                'place_of_worship'
            ];
            return location.types.some(type => validTypes.includes(type));
        });

        return touristLocations;
    } catch (error) {
        logError(error);
        throw error;
    }
};

export const popularLocationService = {
  async getPopularLocations(latitude, longitude, radius = 15000, maxResults = 30) {
    // Önbellekte varsa, önbellekten getir
    const cacheKey = `${latitude}_${longitude}_${radius}_${maxResults}`;
    if (cache.popularLocations && cache.popularLocations[cacheKey]) {
      return cache.popularLocations[cacheKey];
    }
    
    try {
      // Kullanıcı token'ını al
      const userData = JSON.parse(localStorage.getItem('user_data'));
      const token = userData?.token;
      
      // Birden fazla kategori için paralel istekler
      const categories = [
        // Turist yerleri
        {
          endpoint: 'point-of-interest',
          types: ['tourist_attraction', 'museum', 'mosque', 'park', 'place_of_worship', 'zoo', 'aquarium', 'amusement_park']
        },
        // Alışveriş ve eğlence
        {
          endpoint: 'point-of-interest', 
          types: ['shopping_mall', 'department_store', 'clothing_store', 'electronics_store', 'jewelry_store']
        },
        // Yeme içme
        {
          endpoint: 'point-of-interest',
          types: ['restaurant', 'cafe', 'bar', 'night_club', 'meal_takeaway']
        },
        // Spor ve eğlence
        {
          endpoint: 'point-of-interest',
          types: ['gym', 'spa', 'beauty_salon', 'movie_theater', 'bowling_alley']
        },
        // Ulaşım ve hizmetler
        {
          endpoint: 'point-of-interest',
          types: ['transit_station', 'subway_station', 'bus_station', 'hospital', 'pharmacy']
        }
      ];

      const promises = categories.map(category => 
        axios.post(`${config.API_BASE_URL_V1}auto-complete/${category.endpoint}`, 
          {
            latitude,
            longitude,
            radius,
            maxResults: Math.ceil(maxResults / categories.length)
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token ? `Bearer ${token}` : ''
            },
            withCredentials: true
          }
        ).then(response => ({
          data: response.data.data || [],
          validTypes: category.types
        })).catch(error => {
          console.warn(`Failed to fetch ${category.endpoint}:`, error);
          return { data: [], validTypes: category.types };
        })
      );

      const responses = await Promise.all(promises);
      
      // Tüm sonuçları birleştir ve filtrele
      let allLocations = [];
      
      responses.forEach(response => {
        const filteredLocations = response.data.filter(location => {
          return location.types && location.types.some(type => 
            response.validTypes.includes(type)
          );
        });
        allLocations.push(...filteredLocations);
      });

      // Mesafeye göre sırala ve en yakın olanları öne çıkar
      const locationsWithDistance = allLocations.map(location => {
        const distance = calculateDistance(
          latitude, 
          longitude, 
          location.geometry?.location?.lat || 0, 
          location.geometry?.location?.lng || 0
        );
        return { ...location, distance };
      });

      // Sırala: önce mesafe, sonra popülerlik (rating varsa)
      const sortedLocations = locationsWithDistance.sort((a, b) => {
        // Önce önemli turistik yerleri öne çıkar
        const aIsTourist = a.types?.includes('tourist_attraction') || a.types?.includes('museum');
        const bIsTourist = b.types?.includes('tourist_attraction') || b.types?.includes('museum');
        
        if (aIsTourist && !bIsTourist) return -1;
        if (!aIsTourist && bIsTourist) return 1;
        
        // Sonra mesafeye göre sırala
        return a.distance - b.distance;
      });
      
      // Duplicate verileri filtrele
      const filteredData = filterUniqueLocations(sortedLocations);
      
      // İlk N kadar sonucu al
      const finalResults = filteredData.slice(0, maxResults);
      
      // Sonucu önbelleğe kaydet
      if (!cache.popularLocations) {
        cache.popularLocations = {};
      }
      cache.popularLocations[cacheKey] = finalResults;
      
      return finalResults;
    } catch (error) {
      logError(error);
      throw error;
    }
  },
  
  // Önbelleği temizleme
  clearCache() {
    cache.popularLocations = null;
    console.log('Popular location service cache cleared');
  }
};

// Mesafe hesaplama fonksiyonu
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371; // Dünya yarıçapı (km)
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Mesafe (km)
};

export default popularLocationService; 